import { useState, useRef, useMemo, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CheckCircle, XCircle, Clock, FileText, Pen, Type, AlertTriangle, MapPin, Shield, Mail, Loader2, Upload, X, PenTool } from 'lucide-react'
import { publicSigningApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { SignatureType, SignerStatus } from '@/types'

// Geolocation data interface
interface GeolocationData {
  latitude: number
  longitude: number
  accuracy: number
}

const signerStatusIcons: Record<SignerStatus, any> = {
  PENDING: Clock,
  VIEWED: Clock,
  SIGNED: CheckCircle,
  DECLINED: XCircle,
}

// Signer colors for signature markers
const signerColors = [
  { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', badge: '#1d4ed8' },
  { bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', badge: '#047857' },
  { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', badge: '#b45309' },
  { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', badge: '#b91c1c' },
  { bg: 'rgba(139, 92, 246, 0.1)', border: '#8b5cf6', badge: '#6d28d9' },
]

const getSignerColor = (index: number) => signerColors[index % signerColors.length]

export default function PublicSigningPage() {
  const { token } = useParams<{ token: string }>()
  const queryClient = useQueryClient()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [typedName, setTypedName] = useState('')
  const [signatureType, setSignatureType] = useState<SignatureType>('TYPED')
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null)
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [signed, setSigned] = useState(false)
  const [declined, setDeclined] = useState(false)
  const [geolocation, setGeolocation] = useState<GeolocationData | null>(null)
  const [geoPermissionAsked, setGeoPermissionAsked] = useState(false)
  // Consent state
  const [consentGiven, setConsentGiven] = useState(false)
  // OTP verification state
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [, setOtpExpiresAt] = useState<Date | null>(null)
  const [maskedEmail, setMaskedEmail] = useState('')

  // Request geolocation when page loads (optional, with user consent)
  useEffect(() => {
    if (!geoPermissionAsked && navigator.geolocation) {
      setGeoPermissionAsked(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeolocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          })
        },
        () => {
          // User denied or error - geolocation is optional
          setGeolocation(null)
        },
        { timeout: 10000, enableHighAccuracy: false }
      )
    }
  }, [geoPermissionAsked])

  const { data: contract, isLoading, error } = useQuery({
    queryKey: ['publicContract', token],
    queryFn: () => publicSigningApi.getContract(token!),
    enabled: !!token,
    retry: false,
  })

  const signMutation = useMutation({
    mutationFn: () => {
      let signatureData = ''
      if (signatureType === 'DRAWN' && canvasRef.current) {
        signatureData = canvasRef.current.toDataURL()
      } else if (signatureType === 'TYPED') {
        signatureData = typedName
      } else if (signatureType === 'UPLOADED' && uploadedSignature) {
        signatureData = uploadedSignature
      }
      return publicSigningApi.sign(token!, {
        signature_data: signatureData,
        signature_type: signatureType,
        typed_name: signatureType === 'TYPED' ? typedName : undefined,
        // Include geolocation if available (captured with user consent)
        latitude: geolocation?.latitude,
        longitude: geolocation?.longitude,
        geo_accuracy: geolocation?.accuracy,
        // Consent acknowledgment (required)
        consent_given: consentGiven,
      })
    },
    onSuccess: () => {
      setSigned(true)
      toast.success('Contract signed successfully!')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to sign contract'))
    },
  })

  // OTP request mutation
  const requestOTPMutation = useMutation({
    mutationFn: () => publicSigningApi.requestOTP(token!),
    onSuccess: (data) => {
      setOtpSent(true)
      setOtpExpiresAt(new Date(data.expires_at))
      setMaskedEmail(data.masked_email)
      toast.success('Verification code sent to your email')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to send verification code'))
    },
  })

  // OTP verify mutation
  const verifyOTPMutation = useMutation({
    mutationFn: () => publicSigningApi.verifyOTP(token!, { otp_code: otpCode }),
    onSuccess: (data) => {
      if (data.verified) {
        toast.success('Identity verified successfully!')
        // Refresh contract data to get updated verification status
        queryClient.invalidateQueries({ queryKey: ['publicContract', token] })
      } else {
        toast.error(data.message || 'Invalid verification code')
      }
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to verify code'))
    },
  })

  const declineMutation = useMutation({
    mutationFn: () => publicSigningApi.decline(token!, { reason: declineReason }),
    onSuccess: () => {
      setDeclined(true)
      setDeclineDialogOpen(false)
      toast.success('Contract declined')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to decline contract'))
    },
  })

  // Canvas drawing handlers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    setIsDrawing(true)
    const rect = canvas.getBoundingClientRect()
    ctx.beginPath()
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    ctx.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  // Handle signature file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, etc.)')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result as string
      setUploadedSignature(result)
    }
    reader.onerror = () => {
      toast.error('Failed to read the file')
    }
    reader.readAsDataURL(file)
  }

  const clearUploadedSignature = () => {
    setUploadedSignature(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const canSign = () => {
    if (signatureType === 'TYPED') return typedName.trim().length > 0
    if (signatureType === 'DRAWN' && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      if (!ctx) return false
      const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height)
      return imageData.data.some((channel, i) => i % 4 === 3 && channel !== 0)
    }
    if (signatureType === 'UPLOADED') return !!uploadedSignature
    return false
  }

  // Process contract content - just return the content as-is
  // Signature placements are now PDF page-based and rendered separately
  const processedContent = useMemo(() => {
    if (!contract) return ''
    return contract.content
  }, [contract])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground animate-pulse" />
          <p className="mt-4 text-muted-foreground">Loading contract...</p>
        </div>
      </div>
    )
  }

  if (error || !contract) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invalid or Expired Link</h2>
            <p className="text-muted-foreground">
              This signing link is invalid or has expired. Please contact the sender for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (signed || contract.signer_status === 'SIGNED') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Contract Signed!</h2>
            <p className="text-muted-foreground">
              Thank you for signing. You will receive a copy of the signed contract via email.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (declined || contract.signer_status === 'DECLINED') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Contract Declined</h2>
            <p className="text-muted-foreground">
              You have declined to sign this contract. The sender has been notified.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // OTP Verification is mandatory - show verification screen first before showing contract
  if (contract.verification_required && !contract.verification_completed) {
    return (
      <div className="min-h-screen bg-muted/30 py-8">
        <div className="container max-w-md mx-auto space-y-6">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Identity Verification</CardTitle>
              <CardDescription>
                Please verify your identity to view and sign the contract
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">Contract:</p>
                <p className="font-semibold text-lg">{contract.title}</p>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">Signing as:</p>
                <p className="font-medium">{contract.signer_name}</p>
                <p className="text-sm text-muted-foreground">{contract.signer_email}</p>
              </div>

              <div className="border-t pt-6">
                {!otpSent ? (
                  <div className="space-y-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      We'll send a 6-digit verification code to your email address to confirm your identity.
                    </p>
                    <Button
                      onClick={() => requestOTPMutation.mutate()}
                      disabled={requestOTPMutation.isPending}
                      className="w-full"
                      size="lg"
                    >
                      {requestOTPMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Verification Code
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Enter the 6-digit code sent to <strong>{maskedEmail}</strong>
                    </p>
                    <div className="flex flex-col items-center gap-4">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-56 text-center text-2xl tracking-[0.5em] font-mono"
                      />
                      <Button
                        onClick={() => verifyOTPMutation.mutate()}
                        disabled={otpCode.length !== 6 || verifyOTPMutation.isPending}
                        className="w-full"
                        size="lg"
                      >
                        {verifyOTPMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify & Continue'
                        )}
                      </Button>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <span>Didn't receive the code?</span>
                      <Button
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => {
                          setOtpCode('')
                          requestOTPMutation.mutate()
                        }}
                        disabled={requestOTPMutation.isPending}
                      >
                        Resend
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="container max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{contract.title}</CardTitle>
                <CardDescription>Please review and sign this contract</CardDescription>
              </div>
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />
                Awaiting Your Signature
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">Signing as:</p>
              <p className="font-medium">{contract.signer_name} ({contract.signer_email})</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">All Signers:</p>
              <div className="space-y-3">
                {contract.all_signers.map((signer, i) => {
                  const Icon = signerStatusIcons[signer.status]
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <Icon className={`h-5 w-5 mt-0.5 ${signer.status === 'SIGNED' ? 'text-green-500' : signer.status === 'DECLINED' ? 'text-red-500' : 'text-muted-foreground'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{signer.name}</span>
                          <Badge variant={signer.status === 'SIGNED' ? 'default' : 'secondary'} className="text-xs">
                            {signer.status}
                          </Badge>
                        </div>
                        {signer.status === 'SIGNED' && signer.signature_data && (
                          <div className="mt-2 p-2 bg-white rounded border">
                            {signer.signature_type === 'DRAWN' ? (
                              <img
                                src={signer.signature_data}
                                alt={`Signature of ${signer.name}`}
                                className="max-h-16 max-w-[200px]"
                              />
                            ) : (
                              <span className="font-signature text-2xl italic">
                                {signer.typed_name || signer.signature_data}
                              </span>
                            )}
                            {signer.signed_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Signed on {new Date(signer.signed_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contract Content</CardTitle>
          </CardHeader>
          <CardContent>
            {/* A4 Document Container */}
            <div className="max-h-[400px] overflow-auto border rounded-lg bg-muted/30 p-4">
              <div
                className="relative bg-white shadow-md mx-auto"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  padding: '20mm',
                  boxSizing: 'border-box',
                }}
              >
                {/* Document Content */}
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: processedContent }} />

                {/* Signature Placement Markers */}
                {contract.signature_placements?.map((placement, idx) => {
                  const signerIndex = placement.signer_index
                  const signer = contract.all_signers[signerIndex]
                  const colors = getSignerColor(signerIndex)
                  const isSigned = signer?.status === 'SIGNED'
                  return (
                    <div
                      key={`placement-${idx}`}
                      className="absolute flex items-center gap-1 px-2 py-1 rounded border-2 text-xs font-medium shadow-sm"
                      style={{
                        left: `${placement.x_percent}%`,
                        top: `${placement.y_percent}%`,
                        transform: 'translate(-50%, -50%)',
                        borderColor: isSigned ? '#10b981' : colors.border,
                        backgroundColor: isSigned ? 'rgba(16, 185, 129, 0.1)' : colors.bg,
                        color: isSigned ? '#047857' : colors.badge,
                        zIndex: 10,
                      }}
                    >
                      {isSigned ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <PenTool className="h-3 w-3" />
                      )}
                      <span>{signer?.name || `Signer ${signerIndex + 1}`}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Your Signature</CardTitle>
                <CardDescription>Choose how you want to sign this contract</CardDescription>
              </div>
              {geolocation && (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  <MapPin className="h-3 w-3 mr-1" />
                  Location captured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={signatureType} onValueChange={(v) => setSignatureType(v as SignatureType)}>
              <TabsList className="mb-4">
                <TabsTrigger value="TYPED"><Type className="h-4 w-4 mr-2" />Type</TabsTrigger>
                <TabsTrigger value="DRAWN"><Pen className="h-4 w-4 mr-2" />Draw</TabsTrigger>
                <TabsTrigger value="UPLOADED"><Upload className="h-4 w-4 mr-2" />Upload</TabsTrigger>
              </TabsList>

              <TabsContent value="TYPED">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Type your full name</Label>
                    <Input
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Your full legal name"
                      className="text-lg"
                    />
                  </div>
                  {typedName && (
                    <div className="p-4 border rounded-lg bg-white">
                      <p className="text-sm text-muted-foreground mb-2">Signature Preview:</p>
                      <p className="text-3xl font-signature italic">{typedName}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="DRAWN">
                <div className="space-y-4">
                  <div className="border rounded-lg bg-white p-2">
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={200}
                      className="w-full border rounded cursor-crosshair"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={clearCanvas}>Clear</Button>
                </div>
              </TabsContent>

              <TabsContent value="UPLOADED">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Upload your signature image</Label>
                    <p className="text-sm text-muted-foreground">
                      Upload a PNG or JPG image of your signature. For best results, use a signature on a white background.
                    </p>
                  </div>

                  {!uploadedSignature ? (
                    <div
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm font-medium">Click to upload signature</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 border rounded-lg bg-white">
                        <p className="text-sm text-muted-foreground mb-2">Signature Preview:</p>
                        <img
                          src={uploadedSignature}
                          alt="Uploaded signature"
                          className="max-h-24 max-w-full object-contain"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={clearUploadedSignature}
                        className="gap-2"
                      >
                        <X className="h-4 w-4" />
                        Remove & Upload New
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Verification Completed Badge - shown when user has verified their identity */}
        {contract.verification_required && contract.verification_completed && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Identity verified successfully</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consent Checkbox */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="consent"
                checked={consentGiven}
                onCheckedChange={(checked) => setConsentGiven(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="consent" className="text-sm font-medium leading-none cursor-pointer">
                  I agree to sign electronically
                </Label>
                <p className="text-sm text-muted-foreground">
                  {contract.consent_text || 'I agree to sign this document electronically. I understand that my electronic signature is legally binding and has the same legal effect as a handwritten signature.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="destructive" onClick={() => setDeclineDialogOpen(true)}>
            <XCircle className="mr-2 h-4 w-4" />
            Decline to Sign
          </Button>
          <Button
            onClick={() => signMutation.mutate()}
            disabled={
              !canSign() ||
              signMutation.isPending ||
              !consentGiven ||
              (contract.verification_required && !contract.verification_completed)
            }
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            {signMutation.isPending ? 'Signing...' : 'Sign Contract'}
          </Button>
        </div>
      </div>

      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline to Sign</DialogTitle>
            <DialogDescription>Please provide a reason for declining this contract.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason for declining..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => declineMutation.mutate()}
              disabled={!declineReason.trim() || declineMutation.isPending}
            >
              {declineMutation.isPending ? 'Declining...' : 'Confirm Decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

